import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputText } from 'primeng/inputtext';
import { Dialog } from 'primeng/dialog';
import { Pagination } from '@app/shared/components/api/models/pagination.model';
import { DataVoidComponent } from '@app/shared/components/data-void/data-void.component';
import { ConfirmationService, MessageService } from 'primeng/api';
import { PaginatorControlComponent } from '@app/shared/components/paginator-control/paginator-control.component';
import { FaqsService } from '@app/shared/components/api/services/faqs.service';

@Component({
  selector: 'app-perguntas-frequentes',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    InputText,
    Dialog,
    DataVoidComponent,
    PaginatorControlComponent,
  ],
  providers: [FaqsService],
  templateUrl: './perguntas-frequentes.component.html',
  styleUrl: './perguntas-frequentes.component.scss',
})
export class PerguntasFrequentesComponent implements OnInit {
  private readonly faqsService = inject(FaqsService);
  private readonly fb = inject(FormBuilder);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);

  formFAQ!: FormGroup;
  pagination = new Pagination<any>();
  loading: boolean = false;
  showCreateDialog: boolean = false;
  faqID: any;
  selectedFile: File | null = null;
  faqs: any;

  ngOnInit(): void {
    this.initForm();
    this.loadFAQs();
  }

  private initForm(): void {
    this.formFAQ = this.fb.group({
      pergunta: ['', [Validators.required]],
      resposta: ['', [Validators.required]],
    });
  }

  loadFAQs(): void {
    this.loading = true;
    const params = {
      PageSize: this.pagination.pageSize || 5,
      PageNumber: this.pagination.pageNumber || 1,
      OrderBy: 'id desc',
    };

    this.faqsService.list(params).subscribe({
      next: (res) => {
        this.pagination = new Pagination<any>().deserialize(res);

        this.faqs = res?.data.map((f: any) => {
          const [pergunta, resposta] = f.valor.split('|');

          return {
            ...f,
            pergunta,
            resposta,
            open: false,
          };
        });
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        console.error(err);
        this.messageService.add({
          severity: 'error',
          summary: 'Falha',
          detail: err?.error?.message || 'Ocorreu um erro inesperado',
          life: 5000,
        });
      },
    });
  }

  onPageChange(event: any): void {
    this.pagination.pageNumber = event.page + 1;
    this.pagination.pageSize = event.rows;
    this.loadFAQs();
  }

  toggleDialog(): void {
    this.showCreateDialog = !this.showCreateDialog;
  }

  isInvalid(field: string): boolean {
    const control = this.formFAQ.get(field);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  saveFAQ(): void {
    if (this.formFAQ.invalid) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Formulário inválido',
        detail: 'Preencha corretamente o formulário',
      });
      return;
    }

    this.loading = true;

    const payload = {
      Id: 0,
      GestaoPaginaENUM: 19,
      GestaoPaginaTipoDadoENUM: 1,
      Valor: this.formFAQ.value.pergunta + '|' + this.formFAQ.value.resposta,
    };

    const formData = new FormData();

    this.faqsService.store(JSON.stringify(payload), formData).subscribe({
      next: (res) => {
        if (res.retorno.codigo === 200) {
          this.loading = false;
          this.toggleDialog();
          this.formFAQ.reset();
          this.loadFAQs();

          this.messageService.add({
            severity: 'success',
            summary: 'FAQ criada',
            detail: 'FAQ salva com sucesso!',
          });
        } else {
          this.messageService.add({
            severity: 'warn',
            summary: 'Falha',
            detail: 'Falha ao criar FAQ!',
          });
        }
      },
      error: (err) => {
        this.loading = false;
        console.error(err);

        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: err?.error?.retorno?.mensagem || 'Erro ao salvar',
        });
      },
    });
  }

  toggleFAQ(faq: any): void {
    this.confirmationService.confirm({
      message: `Tem certeza que deseja ${faq.ativo ? 'desativar' : 'ativar'} esta FAQ?`,
      header: 'Confirmação',
      closable: true,
      closeOnEscape: true,
      icon: 'pi pi-exclamation-triangle',
      rejectButtonProps: {
        label: 'Cancelar',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: `Sim, ${faq.ativo ? 'Desativar' : 'Ativar'}`,
      },
      accept: () => {
        this.faqsService.toggleStatus(faq?.id).subscribe({
          next: (res) => {
            this.messageService.add({
              severity: 'success',
              summary: 'Sucesso',
              detail: `FAQ ${faq.ativo ? 'desativada' : 'ativada'} com sucesso!`,
              life: 5000,
            });
            this.loadFAQs();
          },
          error: (err) => {
            this.messageService.add({
              severity: 'error',
              summary: 'Erro',
              detail: `Ocorreu um erro ao ${faq.ativo ? 'desativar' : 'ativar'} a FAQ`,
              life: 5000,
            });
            console.error(err);
          },
        });
      },
      reject: () => {},
    });
  }

  deleteFAQ(id: any): void {
    this.confirmationService.confirm({
      message: `Tem certeza que deseja eliminar esta FAQ?`,
      header: 'Confirmação',
      closable: true,
      closeOnEscape: true,
      icon: 'pi pi-exclamation-triangle',
      rejectButtonProps: {
        label: 'Cancelar',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Sim, eliminar',
        severity: 'danger',
      },
      accept: () => {
        this.faqsService.delete(id).subscribe({
          next: (res) => {
            this.messageService.add({
              severity: 'success',
              summary: 'Sucesso',
              detail: `FAQ eliminada com sucesso!`,
              life: 5000,
            });
            this.loadFAQs();
          },
          error: (err) => {
            this.messageService.add({
              severity: 'error',
              summary: 'Erro',
              detail: `Ocorreu um erro ao eliminar a FAQ`,
              life: 5000,
            });
            console.error(err);
          },
        });
      },
      reject: () => {},
    });
  }
}
